import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import './Dashboard.css';
import './AffiliateDash.css';
import { FaCopy, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { APP_CONFIG } from "../../config/appConfig";
import { Link } from 'react-router-dom';
import tracker from '../../utils/tracker';

const BASE_URL = APP_CONFIG.API_BASE_URL;

const AffiliateDash = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const [stats, setStats] = useState({
    referralCount: 0,
    referralsThisWeek: 0,
    totalEarned: 0,
    availableBalance: 0,
    withdrawPending: 0,
    withdrawComplete: 0
  });
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);
  const [reasonModal, setReasonModal] = useState({ show: false, reason: '', status: '' });

  const fetchAffiliateData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Only fetch affiliate stats - refId is already in user context
      const statsResponse = await fetch(`${BASE_URL}/affiliate/stats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!statsResponse.ok) throw new Error('Failed to fetch stats');

      const data = await statsResponse.json();
      setStats({
        referralCount: data.referralCount || 0,
        referralsThisWeek: typeof data.referralsThisWeek === 'number' ? data.referralsThisWeek : 0,
        totalEarned: typeof data.totalEarned === 'number' ? data.totalEarned : 0,
        availableBalance: typeof data.availableBalance === 'number' ? data.availableBalance : 0,
        withdrawPending: typeof data.withdrawPending === 'number' ? data.withdrawPending : 0,
        withdrawComplete: typeof data.withdrawComplete === 'number' ? data.withdrawComplete : 0
      });
      setReferrals(Array.isArray(data.referrals) ? data.referrals : []);
      setWithdrawalHistory(Array.isArray(data.withdrawalHistory) ? data.withdrawalHistory : []);
    } catch (err) {
      console.error('Error fetching affiliate data:', err);
      setStats({
        referralCount: 0,
        referralsThisWeek: 0,
        totalEarned: 0,
        availableBalance: 0,
        withdrawPending: 0,
        withdrawComplete: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Wait until auth state is resolved
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!authLoading) {
      fetchAffiliateData();
    }
  }, [user, authLoading]);

  // Use refId from user context instead of separate state
  const referralLink = user?.refId ? `${APP_CONFIG.BASE_FRONTEND_URL}?ref=${user.refId}` : '';

  const handleCopy = () => {
    if (!referralLink) return;
    tracker.track('ref_link_copied');

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(referralLink)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 1200);
        })
        .catch((err) => {
          console.error('Clipboard API failed:', err);
          fallbackCopy(referralLink);
        });
    } else {
      // Fallback for older browsers and mobile
      fallbackCopy(referralLink);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1200);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Failed to copy link. Please copy manually: ' + text);
    }
    
    document.body.removeChild(textArea);
  };

  const handleCancelWithdrawal = async (withdrawalId) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) {
      return;
    }

    setCancellingId(withdrawalId);
    try {
      const response = await fetch(`${BASE_URL}/withdraw/cancel-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ withdrawalId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to cancel withdrawal');
        return;
      }

      await fetchAffiliateData();
      alert('Withdrawal request cancelled successfully');
    } catch (error) {
      console.error('Error cancelling withdrawal:', error);
      alert('Failed to cancel withdrawal request');
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4ade80';
      case 'pending':
        return '#fbbf24';
      case 'on_hold':
        return '#fb923c';
      case 'rejected':
      case 'cancelled':
        return '#ef4444';
      default:
        return '#9ca3af';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'on_hold':
        return 'On Hold';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  const showReasonModal = (reason, status) => {
    setReasonModal({ show: true, reason, status });
  };

  const closeReasonModal = () => {
    setReasonModal({ show: false, reason: '', status: '' });
  };

  if (authLoading || loading) {
    return (
      <DashboardLayout>
          <div className="loading-spinner"></div>
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
          <div className="no-plan-message">
            <h1>Please sign in</h1>
            <p>You need to be signed in to view the dashboard</p>
            <button 
              className="download-button"
              onClick={() => navigate('/login')}
              style={{ marginTop: '24px' }}
            >
              Sign In
            </button>
          </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
        <div className="affiliate-title-row">
          <h1>Affiliate Program</h1>
          <Link to="/affiliateGuide" className="affiliate-gradient-border" style={{ textDecoration: 'none' }}>
            <div className="affiliate-gradient-button">
              <span className="affiliate-gradient-text">Learn How You Can Earn</span>
            </div>
          </Link>
        </div>

        <div className="affiliate-stats-grid">
          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Total Earned</div>
            <div className="affiliate-stat-value">${stats.totalEarned}</div>
          </div>
        
          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Total Referrals</div>
            <div className="affiliate-stat-value">{stats.referralCount}</div>
          </div>

          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Referrals This Week</div>
            <div className="affiliate-stat-value">{stats.referralsThisWeek}</div>
          </div>
          
          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Available Balance</div>
            <div className="affiliate-stat-value">${stats.availableBalance}</div>
            <div
              className="affiliate-stat-withdraw"
              style={{
                color: stats.availableBalance > 0 ? '#fff' : '#888',
                cursor: stats.availableBalance > 0 ? 'pointer' : 'default',
                marginTop: '6px',
                fontSize: '14px',
                textDecoration: stats.availableBalance > 0 ? 'underline' : 'none'
              }}
              onClick={() => {
                if (stats.availableBalance > 0) {
                  navigate('/withdraw-earnings');
                }
              }}
            >
              Withdraw
            </div>
          </div>
          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Pending Withdrawals</div>
            <div className="affiliate-stat-value">${stats.withdrawPending}</div>
          </div>
          <div className="affiliate-stat-box">
            <div className="affiliate-stat-label">Completed Withdrawals</div>
            <div className="affiliate-stat-value">${stats.withdrawComplete}</div>
          </div>
        </div>

        <div className="affiliate-referral-section">
          <div className="affiliate-referral-label">Referral Link</div>
          <div className="affiliate-referral-row">
            <div className="affiliate-referral-copy-box" onClick={handleCopy}>
              <FaCopy size={22} color={copySuccess ? '#fff' : '#9ca3af'} />
            </div>
            <div className="affiliate-referral-link-box">
              {referralLink}
              {copySuccess && <span style={{ marginLeft: 12, color: '#fff', fontSize: 14 }}>Copied!</span>}
            </div>
          </div>
        </div>

        <div className="affiliate-myreferrals-section">
          <div className="affiliate-myreferrals-label">My Referrals</div>
          <div className="affiliate-myreferrals-list">
            {referrals.length === 0 ? (
              <div className="affiliate-myreferrals-container">
                <div className="affiliate-myreferrals-row">
                  <span className="affiliate-myreferrals-value" style={{ color: '#ABACAA', fontSize: '15px' }}>No referrals yet</span>
                </div>
              </div>
            ) : (
              referrals.map((r, i) => (
                <div className="affiliate-myreferrals-container" key={i}>
                  <div className="affiliate-myreferrals-row">
                    <div className="affiliate-myreferrals-pair">
                      <span className="affiliate-myreferrals-label-grey">Date</span>
                      <span className="affiliate-myreferrals-value">{r.date ? new Date(r.date).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="affiliate-myreferrals-pair">
                      <span className="affiliate-myreferrals-label-grey">Plan</span>
                      <span className="affiliate-myreferrals-value">{r.plan || '-'}</span>
                    </div>
                    <div className="affiliate-myreferrals-pair">
                      <span className="affiliate-myreferrals-label-grey">Earned</span>
                      <span className="affiliate-myreferrals-value">${r.commission || 0}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="affiliate-withdrawals-section">
          <div className="affiliate-withdrawals-label">Withdrawal History</div>
          <div className="affiliate-withdrawals-list">
            {withdrawalHistory.length === 0 ? (
              <div className="affiliate-withdrawals-container">
                <div className="affiliate-withdrawals-row">
                  <span className="affiliate-myreferrals-value" style={{ color: '#ABACAA', fontSize: '15px' }}>No withdrawals yet</span>
                </div>
              </div>
            ) : (
              withdrawalHistory.map((w, i) => (
                <div className="affiliate-withdrawals-container" key={i}>
                  <div className="affiliate-withdrawals-row">
                    <div className="affiliate-withdrawals-pair">
                      <span className="affiliate-myreferrals-label-grey">Date</span>
                      <span className="affiliate-myreferrals-value">{w.date ? new Date(w.date).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="affiliate-withdrawals-pair">
                      <span className="affiliate-myreferrals-label-grey">Amount</span>
                      <span className="affiliate-myreferrals-value">${w.amount || 0}</span>
                    </div>
                    <div className="affiliate-withdrawals-pair">
                      <span className="affiliate-myreferrals-label-grey">Status</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="affiliate-myreferrals-value" style={{ color: getStatusColor(w.status) }}>
                          {getStatusText(w.status)}
                        </span>
                        {w.status === 'pending' && (
                          <FaTimes
                            size={16}
                            style={{
                              color: '#ef4444',
                              cursor: cancellingId === w.id ? 'not-allowed' : 'pointer',
                              opacity: cancellingId === w.id ? 0.5 : 1
                            }}
                            onClick={() => !cancellingId && handleCancelWithdrawal(w.id)}
                            title="Cancel withdrawal request"
                          />
                        )}
                        {(w.status === 'rejected' || w.status === 'on_hold') && w.reason && (
                          <FaInfoCircle
                            size={16}
                            style={{
                              color: '#9ca3af',
                              cursor: 'pointer'
                            }}
                            onClick={() => showReasonModal(w.reason, w.status)}
                            title="View details"
                          />
                        )}
                      </div>
                    </div>
                    <div className="affiliate-withdrawals-pair">
                      <span className="affiliate-myreferrals-label-grey">Method</span>
                      <span className="affiliate-myreferrals-value">{w.blockchain} {w.coin}</span>
                    </div>
                    <div className="affiliate-withdrawals-pair">
                      <span className="affiliate-myreferrals-label-grey">Address</span>
                      <span className="affiliate-myreferrals-value" style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                        {w.address}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {reasonModal.show && (
          <div className="modal-overlay" onClick={closeReasonModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{getStatusText(reasonModal.status)} Details</h2>
                <button className="modal-close" onClick={closeReasonModal}>×</button>
              </div>
              <div className="modal-body">
                <p>{reasonModal.reason}</p>
              </div>
            </div>
          </div>
        )}
    </DashboardLayout>
  );
};

export default AffiliateDash;